import type { ContentCardItem } from '@content-search/domain';
import { InlineAudioPlayer } from '@core/components';
import { ContentType } from '@core/domain';
import { getContentDisplayText, getLanguageName } from '@core/utils';
import { Text, Badge, BadgeSize, contentBlurStyles, BlurToggle } from '@soteria/design-system-ve';
import type { MouseEvent, ReactNode } from 'react';

import * as styles from './ContentCard.css';
import * as imageStyles from './ContentCardImage.css';
import { ImageThumbnail } from './ImageThumbnail';
import { PolicyDisplay } from './PolicyDisplay';

interface SearchStyleContentSummaryProps {
  item: ContentCardItem;
  mediaServiceUrl?: string;
  isContentBlurred: boolean;
  onToggleBlur: (e: MouseEvent) => void;
  /** Optional content to render instead of the default metadata row */
  customHeaderContent?: ReactNode;
}

/**
 * Content summary with image thumbnail, metadata badges, and audio player.
 * Renders with a colored border based on classification result.
 */
// eslint-disable-next-line complexity
export function SearchStyleContentSummary({ item, mediaServiceUrl, isContentBlurred, onToggleBlur, customHeaderContent }: SearchStyleContentSummaryProps) {
  // Determine if content is safe or harmful for border color
  const isSafeContent = item.classificationResult !== 'rejected';
  const borderClass = isSafeContent ? styles.safeContentBorder : styles.unsafeContentBorder;
  const hasImage = Boolean(item.contentUrl) && item.contentType?.toLowerCase() === ContentType.IMAGE;

  return (
    <div className={`${styles.searchContentSummary} ${hasImage ? styles.searchContentSummaryWithImage : ''} ${borderClass}`}>
      {/* Image thumbnail (left side) */}
      {hasImage && (
        <ImageThumbnail
          altText={`Content image for ${item.subjectId}`}
          imageUrl={item.contentUrl || ''}
          isContentBlurred={isContentBlurred}
          isSafe={isSafeContent}
        />
      )}

      {/* Content column */}
      <div className={hasImage ? imageStyles.contentColumn : ''}>
        {/* Primary Transcription Content */}
        <div className={styles.searchTranscriptionContent}>
          <div className={`${styles.textContainer} ${styles.contentBoxContainer}`}>
            <div className={styles.textWrapper}>
              <Text className={`${styles.searchTranscriptionText} ${!isSafeContent && isContentBlurred ? contentBlurStyles.blurredText : ''}`}>
                {getContentDisplayText(item.text, item.contentUrl, item.audioFileUrl)}
              </Text>
            </div>
          </div>
        </div>

        {/* Consolidated Metadata Row with Audio Player */}
        <div className={styles.searchContentHeader}>
          {customHeaderContent || (
            <div className={styles.searchMetadataRow}>
              {item.topicName && item.topicName !== 'Unknown' && (
                <div className={styles.searchMetadataItem}>
                  <span className={styles.searchLabel}>Topic:</span>
                  <Badge size={BadgeSize.Small} text={item.topicName} />
                </div>
              )}
              {item.language && item.language.toLowerCase() !== 'unknown' && (
                <div className={styles.searchMetadataItem}>
                  <span className={styles.searchLabel}>Language:</span>
                  <Badge size={BadgeSize.Small} text={getLanguageName(item.language)} />
                </div>
              )}
              <div className={styles.searchMetadataItem}>
                <span className={styles.searchLabel}>Content Type:</span>
                <Badge size={BadgeSize.Small} text={item.contentType?.toUpperCase() || 'UNKNOWN'} />
              </div>
              {item.labels && item.labels.length > 0 && (
                <div className={styles.searchMetadataItem}>
                  <span className={styles.searchLabel}>Labels:</span>
                  <div className={styles.searchLabelBadges}>
                    {item.labels.map(label => {
                      const displayText = label.name.toLowerCase() === 'safe' ? label.name.toUpperCase() : `${label.name.toUpperCase()} ${label.severity}`;

                      return <Badge key={`${label.name}-${label.severity}`} size={BadgeSize.Small} text={displayText} />;
                    })}
                  </div>
                </div>
              )}
              {item.policyResult && (
                <div className={styles.searchMetadataItem}>
                  <span className={styles.searchLabel}>Triggered Policies:</span>
                  <PolicyDisplay compact policyResult={item.policyResult} />
                </div>
              )}
            </div>
          )}

          {/* Inline Audio Player and Toggle Button - right aligned with metadata */}
          {((item.contentUrl || item.audioFileUrl) && item.contentType?.toLowerCase() === ContentType.AUDIO) || !isSafeContent ? (
            <div className={imageStyles.metadataRightSection}>
              {(item.contentUrl || item.audioFileUrl) && item.contentType?.toLowerCase() === ContentType.AUDIO && (
                <div className={styles.audioPlayerSection}>
                  <InlineAudioPlayer
                    audioId={item.id}
                    audioUrl={item.contentUrl || item.audioFileUrl || ''}
                    mediaServiceUrl={mediaServiceUrl}
                    transcription={item.text}
                  />
                </div>
              )}
              {!isSafeContent && <BlurToggle isBlurred={isContentBlurred} onToggle={onToggleBlur} />}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
